import axios, { AxiosRequestConfig, AxiosResponse, Method, RawAxiosRequestHeaders } from 'axios';
import { API_CONFIG_V2 } from '../../config/api.v2.config';
import { getAccessToken } from '../../utils/util';
import { reportTokenDebug, reportTokenError } from '../../utils/errorReporting';
import axiosRetry from 'axios-retry';

const apiService = axios.create({
  baseURL: API_CONFIG_V2.BASE_URL,
});

axiosRetry(apiService, { retries: 3, retryDelay: axiosRetry.exponentialDelay });

export const handleApiCallV2 = async <T, R>(
  endpoint: string,
  data: T,
  method: Method = 'POST',
  useAccessToken: boolean = false,
  options: AxiosRequestConfig = {}
): Promise<AxiosResponse<R>> => {
  try {
    const headers: RawAxiosRequestHeaders = { ...API_CONFIG_V2.HEADERS };
    if (useAccessToken) {
      const accessToken = getAccessToken();

      reportTokenDebug('API 호출 디버그', {
        endpoint,
        accessToken: accessToken ? `${accessToken.substring(0, 10)}...` : 'NULL/UNDEFINED',
        localStorageValue: localStorage.getItem('accessToken')
          ? `${localStorage.getItem('accessToken')!.substring(0, 10)}...`
          : 'NULL/UNDEFINED',
        useAccessToken,
      });

      if (!accessToken) {
        reportTokenError('엑세스 토큰이 없습니다', {
          endpoint,
          method,
          localStorageHasToken: !!localStorage.getItem('accessToken'),
          localStorageValue: localStorage.getItem('accessToken') ? 'EXISTS' : 'NULL',
        });

        // NOTE: 엑세스 토큰이 필요한데 없을 시 OTP 페이지로 이동
        window.location.href = '/otp';
        throw new Error('엑세스 토큰이 없습니다.');
      }
      headers['auth'] = accessToken;
    }

    if (data instanceof FormData) {
      delete headers['Content-Type'];
    }
    const combinedHeaders = { ...headers, ...options.headers };
    delete options['headers'];

    if (useAccessToken) {
      reportTokenDebug('최종 요청 헤더', {
        endpoint,
        authHeader: combinedHeaders['auth']
          ? `${combinedHeaders['auth'].substring(0, 10)}...`
          : 'NULL/UNDEFINED',
      });
    }

    const response = await apiService.request<R>({
      url: endpoint,
      method,
      data: method === 'GET' ? undefined : data,
      params: method === 'GET' ? data : undefined,
      headers: combinedHeaders,
      ...options,
    });
    return response;
  } catch (error) {
    throw error;
  }
};
